import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer, Tooltip, message } from 'antd';
import {
  ApiOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  DisconnectOutlined,
  LinkOutlined,
  LoadingOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { GoogleUser } from '../App';
import type { ConnectorInstance, ConnectorType } from '../api/client';
import * as api from '../api/client';
import {
  CONNECTOR_OAUTH_EVENT,
  CONNECTOR_OAUTH_MESSAGE_TYPE,
  CONNECTOR_OAUTH_RESULT_KEY,
  getConnectorOAuthRedirectUri,
  rememberConnectorOAuthPopup,
} from '../utils/connectorOAuth';

interface Props {
  user: GoogleUser | null;
}

const CONNECTOR_DOCS: Record<string, { summary: string; setupHint: string; docsUrl: string }> = {
  github: {
    summary: 'Read repositories, issues, and pull requests via a GitHub App installation.',
    setupHint: 'Create a GitHub App in your organisation or account settings, then install it on the repos you want Praxys to access.',
    docsUrl: 'https://docs.github.com/en/apps/creating-github-apps',
  },
  slack: {
    summary: 'Read channels and messages from your Slack workspace and send task notifications.',
    setupHint: 'Create a Slack App, add https://praxys.ai/ under OAuth & Permissions redirect URLs, and add the required Bot Token Scopes.',
    docsUrl: 'https://api.slack.com/apps',
  },
  gdrive: {
    summary: 'Mirror uploaded files to Google Drive under Praxys.ai/Tasks and Praxys.ai/Chats.',
    setupHint: 'Use the Google Cloud OAuth client that already allows https://praxys.ai/ as the redirect URI.',
    docsUrl: 'https://console.cloud.google.com/apis/credentials',
  },
  gcalendar: {
    summary: 'Connect Google Calendar so agents can read calendars and work with events.',
    setupHint: 'Use the same Google Cloud OAuth client as Drive, with Google Calendar API enabled and Calendar scopes allowed.',
    docsUrl: 'https://console.cloud.google.com/apis/library/calendar-json.googleapis.com',
  },
  teams: {
    summary: 'Send Teams notifications through an incoming webhook and read Teams metadata with a Graph access token.',
    setupHint: 'Provide an incoming webhook URL, a Microsoft Graph bearer token, or both. The Test action validates the saved setup.',
    docsUrl: 'https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook',
  },
  facebook: {
    summary: 'Connect a Facebook Page for marketing posts, engagement checks, and comment replies.',
    setupHint: 'Use a Meta Developer app with the Page management use case, then add the domain and redirect under Facebook Login for Business.',
    docsUrl: 'https://developers.facebook.com/documentation/pages-api/overview',
  },
  linkedin: {
    summary: 'Connect LinkedIn for member posts, and organization posts when your LinkedIn app has the required products.',
    setupHint: 'Create a LinkedIn Developer app, add Sign In with LinkedIn using OpenID Connect and Share on LinkedIn, then allow https://praxys.ai/ as the redirect URL.',
    docsUrl: 'https://learn.microsoft.com/en-us/linkedin/shared/authentication/authorization-code-flow',
  },
  x: {
    summary: 'Connect an X account for posts, engagement checks, and replies.',
    setupHint: 'Create an X Developer app, enable OAuth 2.0 Authorization Code with PKCE, add tweet/user scopes, and allow https://praxys.ai/ as the callback URL.',
    docsUrl: 'https://docs.x.com/fundamentals/authentication/oauth-2-0/authorization-code',
  },
};

const FALLBACK_TYPES: ConnectorType[] = [
  { type: 'github', name: 'GitHub', icon: '🐙', auth_type: 'github_app', scopes: [], description: '' },
  { type: 'slack', name: 'Slack', icon: '💬', auth_type: 'oauth2', scopes: [], description: '' },
  { type: 'gdrive', name: 'Google Drive', icon: '📁', auth_type: 'oauth2', scopes: [], description: '' },
  { type: 'gcalendar', name: 'Google Calendar', icon: 'Cal', auth_type: 'oauth2', scopes: [], description: '' },
  { type: 'teams', name: 'Microsoft Teams', icon: 'T', auth_type: 'webhook_or_token', scopes: [], description: '' },
  { type: 'facebook', name: 'Facebook', icon: 'f', auth_type: 'oauth2', scopes: [], description: '' },
  { type: 'linkedin', name: 'LinkedIn', icon: 'in', auth_type: 'oauth2', scopes: [], description: '' },
  { type: 'x', name: 'X', icon: 'X', auth_type: 'oauth2', scopes: [], description: '' },
];

const formatTimestamp = (value?: string) => {
  if (!value) return '—';
  const timestamp = new Date(value);
  return Number.isNaN(timestamp.getTime()) ? '—' : timestamp.toLocaleString();
};

const statusBadge = (instance: ConnectorInstance) => {
  const palette =
    instance.status === 'connected'
      ? { color: '#10b981', background: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.24)', label: 'Connected', icon: <CheckCircleOutlined /> }
      : instance.status === 'error'
        ? { color: '#f87171', background: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.24)', label: 'Error', icon: <DisconnectOutlined /> }
        : { color: '#94a3b8', background: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.24)', label: 'Pending', icon: <ReloadOutlined /> };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        border: `1px solid ${palette.border}`,
        background: palette.background,
        color: palette.color,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {palette.icon}
      {palette.label}
    </span>
  );
};

const authLabel = (authType?: string) => {
  if (authType === 'github_app') return 'GitHub App';
  if (authType === 'webhook_or_token') return 'Webhook / Token';
  if (authType === 'api_key') return 'Token';
  return 'OAuth 2.0';
};

const ProfileConnectorsView: React.FC<Props> = ({ user }) => {
  const userId = user?.email ?? '';
  const [types, setTypes] = useState<ConnectorType[]>([]);
  const [instances, setInstances] = useState<ConnectorInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [connectorSearch, setConnectorSearch] = useState('');
  const [connectingType, setConnectingType] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [manualConnector, setManualConnector] = useState<ConnectorType | null>(null);
  const [teamsWebhookUrl, setTeamsWebhookUrl] = useState('');
  const [teamsAccessToken, setTeamsAccessToken] = useState('');
  const [savingManualConnector, setSavingManualConnector] = useState(false);

  const connectorTypes = useMemo(
    () => {
      const byType = new Map(FALLBACK_TYPES.map(type => [type.type, type]));
      types.forEach(type => byType.set(type.type, type));
      return Array.from(byType.values());
    },
    [types],
  );

  const availableConnectorTypes = useMemo(
    () => {
      const existingTypes = new Set(instances.map(instance => instance.connector_type));
      const query = connectorSearch.trim().toLowerCase();
      return connectorTypes.filter(type => {
        if (existingTypes.has(type.type)) return false;
        if (!query) return true;
        return (
          type.name.toLowerCase().includes(query) ||
          type.type.toLowerCase().includes(query) ||
          (type.description || '').toLowerCase().includes(query)
        );
      });
    },
    [connectorSearch, connectorTypes, instances],
  );
  const selectedConnector: ConnectorType | undefined = undefined;

  const load = useCallback(async () => {
    if (!userId) {
      setInstances([]);
      return;
    }
    setLoading(true);
    try {
      const [typesRes, instancesRes] = await Promise.allSettled([
        api.listConnectorTypes(),
        api.listConnectorInstances(userId),
      ]);
      if (typesRes.status === 'fulfilled') {
        setTypes(typesRes.value.data.connector_types ?? []);
      }
      if (instancesRes.status === 'fulfilled') {
        setInstances(instancesRes.value.data.instances ?? []);
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== CONNECTOR_OAUTH_MESSAGE_TYPE) return;
      if (event.data?.error) {
        message.error(event.data.error);
      } else {
        message.success('Connector updated');
      }
      void load();
    };

    const handleOAuthComplete = () => {
      void load();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== CONNECTOR_OAUTH_RESULT_KEY || !event.newValue) return;
      try {
        const result = JSON.parse(event.newValue);
        if (result?.payload?.error) {
          message.error(result.payload.error);
        } else {
          message.success('Connector updated');
        }
      } catch {
        message.success('Connector updated');
      }
      void load();
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener(CONNECTOR_OAUTH_EVENT, handleOAuthComplete as EventListener);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener(CONNECTOR_OAUTH_EVENT, handleOAuthComplete as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, [load]);

  const openConnectFlow = async (connectorType: string) => {
    if (!userId) return;
    const connectorMeta = connectorTypes.find(type => type.type === connectorType);
    if (connectorMeta?.auth_type === 'webhook_or_token' || connectorMeta?.auth_type === 'api_key') {
      setManualConnector(connectorMeta);
      setTeamsWebhookUrl('');
      setTeamsAccessToken('');
      setDrawerOpen(true);
      return;
    }
    setConnectingType(connectorType);
    try {
      const redirectUri = getConnectorOAuthRedirectUri();
      const res = await api.createConnectorInstance({
        connector_type: connectorType,
        name: connectorMeta?.name,
        redirect_uri: redirectUri,
      });
      const authUrl = res.data.auth_url;
      if (!authUrl) {
        message.error('Could not get an authorisation URL');
        return;
      }

      setDrawerOpen(false);
      setConnectorSearch('');
      rememberConnectorOAuthPopup(authUrl);
      const popup = window.open(authUrl, `${connectorType}_oauth`, 'width=640,height=720,toolbar=0,menubar=0');
      if (!popup) {
        window.location.assign(authUrl);
        return;
      }
      const timer = window.setInterval(() => {
        if (popup.closed) {
          window.clearInterval(timer);
          window.setTimeout(() => void load(), 1200);
        }
      }, 500);
    } catch (error: any) {
      message.error(`Connection failed: ${error?.response?.data?.detail ?? error?.message ?? 'unknown error'}`);
    } finally {
      setConnectingType(null);
    }
  };

  const handleSaveManualConnector = async () => {
    if (!manualConnector) return;
    if (!teamsWebhookUrl.trim() && !teamsAccessToken.trim()) {
      message.warning('Provide a webhook URL, access token, or both');
      return;
    }
    setSavingManualConnector(true);
    try {
      const res = await api.createManualConnectorInstance({
        connector_type: manualConnector.type,
        name: manualConnector.name,
        webhook_url: teamsWebhookUrl.trim(),
        access_token: teamsAccessToken.trim(),
      });
      if (res.data.ok) {
        message.success(`${manualConnector.name} connected`);
      } else {
        message.warning(`${manualConnector.name} saved, but the connection test failed`);
      }
      setManualConnector(null);
      setTeamsWebhookUrl('');
      setTeamsAccessToken('');
      setDrawerOpen(false);
      setConnectorSearch('');
      await load();
    } catch (error: any) {
      message.error(`Connection failed: ${error?.response?.data?.detail ?? error?.message ?? 'unknown error'}`);
    } finally {
      setSavingManualConnector(false);
    }
  };

  const handleTest = async (instanceId: string) => {
    setTestingId(instanceId);
    try {
      const res = await api.testConnectorInstance(instanceId);
      if (res.data.ok) {
        message.success('Connection is healthy');
      } else {
        message.warning('Connection test failed');
      }
      await load();
    } catch {
      message.error('Connection test failed');
    } finally {
      setTestingId(null);
    }
  };

  const handleDisconnect = async (instanceId: string) => {
    setDisconnectingId(instanceId);
    try {
      await api.deleteConnectorInstance(instanceId);
      message.success('Connector disconnected');
      await load();
    } catch (error: any) {
      message.error(`Disconnect failed: ${error?.response?.data?.detail ?? error?.message ?? 'unknown error'}`);
    } finally {
      setDisconnectingId(null);
    }
  };

  const connectedCount = instances.filter(instance => instance.status === 'connected').length;
  const errorCount = instances.filter(instance => instance.status === 'error').length;

  return (
    <section className="praxys-apikey-card">
      <div className="praxys-apikey-card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ApiOutlined style={{ color: '#6366f1' }} />
          <h3 className="praxys-apikey-card-title" style={{ margin: 0 }}>Connectors</h3>
          {(connectedCount > 0 || errorCount > 0) && (
            <span style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>
              {connectedCount} connected
              {errorCount > 0 && <span style={{ marginLeft: 6, color: '#f87171' }}>· {errorCount} need attention</span>}
            </span>
          )}
        </div>
        <button
          type="button"
          className="praxys-apikey-add-btn"
          onClick={() => setDrawerOpen(true)}
          title="Add connector"
          aria-label="Add connector"
        >
          <PlusOutlined />
        </button>
      </div>

      <p style={{ margin: '0 0 14px', fontSize: 12, color: '#888', lineHeight: 1.5 }}>
        Manage Google Drive, Google Calendar, Microsoft Teams, Slack, and GitHub access from your profile. New connector sign-ins return to{' '}
        <code className="praxys-secrets-inline-code">https://praxys.ai/</code>{' '}
        before Praxys finishes the connection in the background.
      </p>

      {loading ? (
        <div className="praxys-view-empty">Loading connectors…</div>
      ) : instances.length === 0 ? (
        <div className="praxys-view-empty">
          No connectors yet.{' '}
          <button type="button" className="praxys-apikey-inline-link" onClick={() => setDrawerOpen(true)}>
            Add your first connector
          </button>{' '}
          to get started.
        </div>
      ) : (
        <div className="praxys-apikey-table-wrapper">
          <table className="praxys-apikey-table" aria-label="Connectors">
            <thead>
              <tr className="praxys-apikey-thead-row">
                {['Connector', 'Status', 'Account', 'Last Synced', 'Auth', ''].map(header => (
                  <th key={header} className={`praxys-apikey-th${header === '' ? ' praxys-apikey-th--options' : ''}`}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {instances.map(instance => {
                const connectorMeta = connectorTypes.find(type => type.type === instance.connector_type);
                const docs = CONNECTOR_DOCS[instance.connector_type];
                return (
                  <tr key={instance.id} className={`praxys-apikey-row${instance.status === 'connected' ? '' : ' praxys-apikey-row--inactive'}`}>
                    <td className="praxys-apikey-cell">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 18 }}>{connectorMeta?.icon ?? '🔌'}</span>
                          <div style={{ fontWeight: 600, color: '#e5e7eb' }}>{connectorMeta?.name ?? instance.name}</div>
                        </div>
                        <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
                          {docs?.summary || 'First-party connector'}
                        </div>
                        {instance.error_message && (
                          <div style={{ fontSize: 12, color: '#f87171', lineHeight: 1.5 }}>
                            {instance.error_message}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="praxys-apikey-cell">{statusBadge(instance)}</td>
                    <td className="praxys-apikey-cell" style={{ color: '#cbd5e1' }}>{instance.name || connectorMeta?.name || '—'}</td>
                    <td className="praxys-apikey-cell" style={{ color: '#94a3b8' }}>{formatTimestamp(instance.last_synced_at)}</td>
                    <td className="praxys-apikey-cell" style={{ color: '#94a3b8' }}>
                      {authLabel(connectorMeta?.auth_type)}
                    </td>
                    <td className="praxys-apikey-cell praxys-apikey-cell--options">
                      <div className="praxys-apikey-options">
                        <Tooltip title="Reconnect">
                          <button
                            type="button"
                            onClick={() => void openConnectFlow(instance.connector_type)}
                            className="praxys-apikey-icon-btn"
                            disabled={connectingType === instance.connector_type}
                            style={{ color: '#818cf8' }}
                          >
                            {connectingType === instance.connector_type ? <LoadingOutlined /> : <LinkOutlined />}
                          </button>
                        </Tooltip>
                        <Tooltip title="Test">
                          <button
                            type="button"
                            onClick={() => void handleTest(instance.id)}
                            className="praxys-apikey-icon-btn"
                            disabled={testingId === instance.id}
                          >
                            {testingId === instance.id ? <LoadingOutlined /> : <ReloadOutlined />}
                          </button>
                        </Tooltip>
                        <Tooltip title="Disconnect">
                          <button
                            type="button"
                            onClick={() => void handleDisconnect(instance.id)}
                            className="praxys-apikey-icon-btn praxys-apikey-icon-btn--danger"
                            disabled={disconnectingId === instance.id}
                            style={{ color: '#f87171' }}
                          >
                            {disconnectingId === instance.id ? <LoadingOutlined /> : <DeleteOutlined />}
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Drawer
        className="praxys-secrets-drawer"
        placement="right"
        width={460}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setConnectorSearch('');
          setManualConnector(null);
          setTeamsWebhookUrl('');
          setTeamsAccessToken('');
        }}
        destroyOnClose
        title={(
          <span className="praxys-secrets-drawer-title">
            <ApiOutlined />
            Add connector
          </span>
        )}
      >
        <div className="praxys-secrets-drawer-content">
          <div className="praxys-secrets-drawer-body">
            <p className="praxys-secrets-drawer-subtitle">
              Choose a connector and start the same guided flow used by the rest of your profile settings.
            </p>

            <div className="praxys-secrets-field" style={{ marginBottom: 18 }}>
              <div className="praxys-secrets-search">
                <SearchOutlined className="praxys-secrets-search-icon" />
                <input
                  id="connector-search"
                  type="search"
                  value={connectorSearch}
                  onChange={(event) => setConnectorSearch(event.target.value)}
                  placeholder="Search by name..."
                  className="praxys-secrets-filter-control"
                />
              </div>
            </div>

            {manualConnector && (
              <div
                style={{
                  border: '1px solid #2f3541',
                  borderRadius: 14,
                  padding: 16,
                  background: '#111827',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                  marginBottom: 12,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 24 }}>{manualConnector.icon || 'T'}</span>
                  <div>
                    <div style={{ color: '#f8fafc', fontSize: 15, fontWeight: 600 }}>{manualConnector.name}</div>
                    <div style={{ color: '#94a3b8', fontSize: 12 }}>{authLabel(manualConnector.auth_type)}</div>
                  </div>
                </div>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ color: '#cbd5e1', fontSize: 12, fontWeight: 600 }}>Incoming webhook URL</span>
                  <input
                    type="password"
                    value={teamsWebhookUrl}
                    onChange={(event) => setTeamsWebhookUrl(event.target.value)}
                    placeholder="https://*.webhook.office.com/..."
                    className="praxys-secrets-filter-control"
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ color: '#cbd5e1', fontSize: 12, fontWeight: 600 }}>Microsoft Graph access token</span>
                  <textarea
                    value={teamsAccessToken}
                    onChange={(event) => setTeamsAccessToken(event.target.value)}
                    placeholder="Bearer token value"
                    className="praxys-secrets-filter-control"
                    rows={4}
                    style={{ resize: 'vertical', minHeight: 96 }}
                  />
                </label>
                <div style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.5 }}>
                  Save will immediately test the connector. Webhook tests send a small card to the configured Teams channel.
                </div>
              </div>
            )}

            <div style={{ display: manualConnector ? 'none' : 'flex', flexDirection: 'column', gap: 10 }}>
              {availableConnectorTypes.length === 0 ? (
                <div className="praxys-view-empty" style={{ marginTop: 4 }}>
                  {connectorSearch.trim()
                    ? 'No available connectors match your search.'
                    : 'All available connectors are already connected.'}
                </div>
              ) : (
                availableConnectorTypes.map(type => {
                  const docs = CONNECTOR_DOCS[type.type];
                  const isConnecting = connectingType === type.type;
                  return (
                    <button
                      key={type.type}
                      type="button"
                      onClick={() => void openConnectFlow(type.type)}
                      disabled={!!connectingType}
                      aria-label={`Connect ${type.name}`}
                      style={{
                        width: '100%',
                        border: '1px solid #2f3541',
                        borderRadius: 14,
                        padding: 16,
                        background: '#111827',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 12,
                        color: 'inherit',
                        textAlign: 'left',
                        cursor: connectingType ? 'not-allowed' : 'pointer',
                        opacity: connectingType && !isConnecting ? 0.55 : 1,
                      }}
                    >
                      <span style={{ fontSize: 24, lineHeight: 1 }}>{type.icon || '🔌'}</span>
                      <span style={{ display: 'flex', flex: 1, minWidth: 0, flexDirection: 'column', gap: 6 }}>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                          <span style={{ color: '#f8fafc', fontSize: 15, fontWeight: 600 }}>{type.name}</span>
                          <span style={{ color: '#94a3b8', fontSize: 12, whiteSpace: 'nowrap' }}>
                            {isConnecting ? <LoadingOutlined /> : authLabel(type.auth_type)}
                          </span>
                        </span>
                        <span style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.5 }}>
                          {docs?.summary || type.description || 'First-party connector'}
                        </span>
                        {docs?.setupHint && (
                          <span style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.5 }}>
                            {docs.setupHint}
                          </span>
                        )}
                        {docs?.docsUrl && (
                          <a
                            href={docs.docsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            style={{ color: '#818cf8', fontSize: 12, fontWeight: 600 }}
                          >
                            Review setup docs
                          </a>
                        )}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            {false && selectedConnector && (
              <div
                style={{
                  border: '1px solid #2f3541',
                  borderRadius: 14,
                  padding: 16,
                  background: '#111827',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 24 }}>{selectedConnector.icon || '🔌'}</span>
                  <div>
                    <div style={{ color: '#f8fafc', fontSize: 15, fontWeight: 600 }}>{selectedConnector.name}</div>
                    <div style={{ color: '#94a3b8', fontSize: 12 }}>
                      {authLabel(selectedConnector.auth_type)}
                    </div>
                  </div>
                </div>
                <div style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.6 }}>
                  {CONNECTOR_DOCS[selectedConnector.type]?.summary || selectedConnector.description || 'First-party connector'}
                </div>
                <div style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.6 }}>
                  {CONNECTOR_DOCS[selectedConnector.type]?.setupHint}
                </div>
                {CONNECTOR_DOCS[selectedConnector.type]?.docsUrl && (
                  <a
                    href={CONNECTOR_DOCS[selectedConnector.type].docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#818cf8', fontSize: 12, fontWeight: 600 }}
                  >
                    Review setup docs ↗
                  </a>
                )}
              </div>
            )}
          </div>

          <div className="praxys-secrets-drawer-footer">
            <button
              onClick={() => {
                setDrawerOpen(false);
                setConnectorSearch('');
                setManualConnector(null);
                setTeamsWebhookUrl('');
                setTeamsAccessToken('');
              }}
              className="praxys-secrets-drawer-btn praxys-secrets-drawer-btn--secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (manualConnector) void handleSaveManualConnector();
                else if (selectedConnector) void openConnectFlow(selectedConnector.type);
              }}
              className="praxys-secrets-drawer-btn praxys-secrets-drawer-btn--primary"
              disabled={manualConnector ? savingManualConnector : (!selectedConnector || connectingType === selectedConnector.type)}
              style={manualConnector ? undefined : { display: 'none' }}
            >
              {savingManualConnector ? 'Testing...' : 'Save connector'}
            </button>
          </div>
        </div>
      </Drawer>
    </section>
  );
};

export default ProfileConnectorsView;
